import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import Button from '@components/common/Button';
import { Checkbox } from '@components/common';
import type { AssessorLoadingAssessor } from '@utils/assessorLoadingChart';
import styles from './AssessorFilterDropdown.module.css';

interface AssessorFilterDropdownProps {
  assessors: AssessorLoadingAssessor[];
  /** Empty means every Assessor is shown. */
  selectedAssessorIds: number[];
  onSelectionChange: (assessorIds: number[]) => void;
}

/** Narrows the Assessor Loading chart to chosen Assessors; each option shows that Assessor's Meetings in the month. */
const AssessorFilterDropdown: React.FC<AssessorFilterDropdownProps> = ({
  assessors,
  selectedAssessorIds,
  onSelectionChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleAssessor = (assessorId: number) => {
    if (selectedAssessorIds.includes(assessorId)) {
      onSelectionChange(selectedAssessorIds.filter((id) => id !== assessorId));
    } else {
      onSelectionChange([...selectedAssessorIds, assessorId]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={styles.dropdown} ref={dropdownRef} data-test-id="assessor-loading-assessor-filter">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        dataTestId="assessor-loading-assessor-filter-toggle"
      >
        {selectedAssessorIds.length > 0 ? `Assessors (${selectedAssessorIds.length})` : 'All Assessors'}
        <ChevronDown size={16} className={styles.icon} />
      </Button>

      {isOpen && (
        <div className={styles.menu} data-test-id="assessor-loading-assessor-filter-menu">
          <div className={styles.menuHeader}>
            <span>Select Assessors</span>
            {selectedAssessorIds.length > 0 && (
              <button type="button" onClick={() => onSelectionChange([])} className={styles.clear}>
                Show all
              </button>
            )}
          </div>
          <div className={styles.menuContent}>
            {assessors.map((assessor) => (
              <div key={assessor.id} className={styles.option}>
                <Checkbox
                  checked={selectedAssessorIds.includes(assessor.id)}
                  onChange={() => toggleAssessor(assessor.id)}
                  label={assessor.name}
                  wrapperClassName={styles.checkboxWrapper}
                />
                <span className={styles.count}>{assessor.monthTotal}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export { AssessorFilterDropdown };
